import { getGlobalProperty } from "@faire/web/common/globals/getGlobalProperty";
import { getLocation } from "@faire/web/common/globals/getLocation";
import { pathMatchesRoute } from "@faire/web/common/routes/RouteMatcher";
import { shouldUseAnchorForLink } from "@faire/web/ui/lib";
import { route as supportRoute } from "@faire/web-api--source/routes/www/support";
import { HTMLAttributeAnchorTarget } from "react";

/**
 * Sanitizes a link to be used in a button by stripping the origin if it is an internal link.
 *
 * This function is used to ensure that internal urls are stripped of their origin,
 * to avoid page reloads when the link is clicked.
 *
 * Exceptions would be "/blog" and "/support" as these are links pages require a full page reload.
 *
 * ```ts
 * // location is https://www.faire.com
 * sanitizeLink("https://www.faire.com/categories") // "/categories"
 *
 * sanitizeLink("https://www.another-domain.com/categories") // "https://www.another-domain.com/categories"
 *
 * sanitizeLink("https://www.faire.com/blog") // "https://www.faire.com/blog"
 * sanitizeLink("https://www.faire.com/support") // "https://www.faire.com/support"
 * ```
 *
 * @param linkTo - The link to sanitize.
 * @param target - The target of the link.
 * @returns The sanitized link.
 */

export const sanitizeLink = <T>(
  linkTo?: T,
  target?: HTMLAttributeAnchorTarget
) => {
  const app = getGlobalProperty("faire.app");
  const location = getLocation();

  if (
    !location ||
    target === "_blank" ||
    app !== "web-retailer" ||
    !shouldUseAnchorForLink(linkTo)
  ) {
    return linkTo as T;
  }

  try {
    const url = new URL(linkTo);

    if (isFromSkipList(url)) {
      return linkTo;
    }

    if (!isFromEligibleOrigin(url)) {
      return linkTo;
    }

    const sanitizedLink = linkTo.replace(url.origin, "");
    return sanitizedLink === "" ? "/" : sanitizedLink;
  } catch (_) {
    // we should only perform domain stripping on absolute links
    // this will catch the error throw from `new URL(linkTo)` for href is not an absolute link
    return linkTo;
  }
};

const isFromSkipList = (url: URL) => {
  return EXCLUDED_PATHNAMES.some((excludedPathname) =>
    pathMatchesRoute(url.pathname, excludedPathname)
  );
};

const isFromEligibleOrigin = (url: URL) => {
  const location = getLocation();

  if (!location) {
    return false;
  }

  const currentLocationOrigin = location.origin;
  const isLocalhost = LOCAL_HOSTNAME.test(currentLocationOrigin);
  const isEligibleOrigin =
    url.origin === currentLocationOrigin ||
    (isLocalhost && url.origin === STAGING_HOSTNAME);

  return isEligibleOrigin;
};

const STAGING_HOSTNAME = "https://www.faire-stage.com";
const LOCAL_HOSTNAME = /^http:\/\/localhost:300[013]$/;
const EXCLUDED_PATHNAMES = [supportRoute, "/blog"];
